import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton, IconButtonProps } from 'src/components/IconButton';
import { Material } from 'src/components/Material';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import {
  isBXMobileWebView,
  setBxMobileTitle,
} from 'src/utils/webview-detection';

import type { ButtonPropsWithLabel } from './AppFrame';

export interface BxMobileActionsProps {
  title: React.ReactNode;
  collapsedTitle?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  bxMobileActionStyle?: 'fab' | 'header';
}

const renderFrameActions = (
  actions?: React.ReactNode | ButtonPropsWithLabel[]
) => {
  if (Array.isArray(actions)) {
    return actions.map((buttonProps, i) => {
      if (buttonProps.icon) {
        const { icon, label: _label, ...iconButtonProps } = buttonProps;

        return (
          <IconButton key={i} {...(iconButtonProps as IconButtonProps)}>
            <Icon color="action" size="medium" icon={icon} />
          </IconButton>
        );
      }

      return (
        <Button key={i} {...buttonProps}>
          {buttonProps.label}
        </Button>
      );
    });
  }

  return actions;
};

export const BxMobileActions: React.FC<BxMobileActionsProps> = ({
  title,
  collapsedTitle,
  actions,
  bxMobileActionStyle,
}) => {
  const {
    ref: onPageTitleRef,
    inView: onPageTitleInView,
    entry,
  } = useInView({
    threshold: 1,
  });

  useEffect(() => {
    if (!isBXMobileWebView() || bxMobileActionStyle === 'header') {
      return;
    }

    if (onPageTitleInView || !title) {
      setBxMobileTitle(null);
    } else {
      // BxMobile expects a string title so we will use the heading text if available
      // else use the full title text
      const titleElem = entry?.target;
      const titleFullText = titleElem?.textContent || '';
      const titleHeadingText = titleElem?.querySelector(
        'h1, h2, h3, h4, h5, h6'
      )?.textContent;
      setBxMobileTitle(titleHeadingText || titleFullText);
    }
  }, [bxMobileActionStyle, entry?.target, onPageTitleInView, title]);

  if (!isBXMobileWebView() || !bxMobileActionStyle) {
    return null;
  }

  const spacerTitleSx = {
    px: 2,
    height: '56px',
    display: 'flex',
    alignItems: 'center',
  };

  const actionStack = (
    <Stack direction="row-reverse" alignItems="flex-start" spacing={1}>
      {renderFrameActions(actions)}
    </Stack>
  );

  if (bxMobileActionStyle === 'header') {
    return (
      <>
        <Typography variant="h6" sx={spacerTitleSx}></Typography>
        <Material
          variant="regular"
          sx={{
            p: 0.5,
            mb: 2,
            position: 'fixed',
            top: '10px',
            left: '12px',
            zIndex: 10,
            border: '1px solid',
            borderColor: 'divider',
            width: 'calc(100% - 24px)',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" sx={{ ml: 1 }}>
              {collapsedTitle || title}
            </Typography>
            {actionStack}
          </Stack>
        </Material>
      </>
    );
  }

  return (
    <>
      <Typography
        noWrap
        variant="h5"
        sx={{ mt: 2, mb: 2, px: 2, textTransform: 'capitalize' }}
        ref={onPageTitleRef}
      >
        {title}
      </Typography>

      <Material
        variant="regular"
        sx={{
          p: 0.5,
          position: 'fixed',
          bottom: '21px',
          right: '12px',
          zIndex: 10,
          border: '1px solid',
          borderColor: 'neutral.4',
          boxShadow: (theme) => theme.shadows[4],
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {actionStack}
        </Stack>
      </Material>
    </>
  );
};
